import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useState,
} from 'react';
import { Project } from '@avanzu/types';
import { apiClient } from '../services/apiClient';
import { useAuth } from './AuthContext';

interface ProjectState {
  currentProject: Project | null;
  currentProjectId: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface ProjectContextType extends ProjectState {
  setCurrentProject: (projectId: string) => Promise<void>;
  clearCurrentProject: () => void;
  refreshCurrentProject: () => Promise<void>;
}

enum ProjectActionTypes {
  SET_LOADING = 'SET_LOADING',
  SET_PROJECT = 'SET_PROJECT',
  SET_ERROR = 'SET_ERROR',
  CLEAR_PROJECT = 'CLEAR_PROJECT',
  SET_INITIALIZED = 'SET_INITIALIZED',
}

type ProjectAction =
  | { type: ProjectActionTypes.SET_LOADING; payload: boolean }
  | { type: ProjectActionTypes.SET_PROJECT; payload: { project: Project; projectId: string } }
  | { type: ProjectActionTypes.SET_ERROR; payload: string | null }
  | { type: ProjectActionTypes.CLEAR_PROJECT }
  | { type: ProjectActionTypes.SET_INITIALIZED; payload: boolean };

function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case ProjectActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ProjectActionTypes.SET_PROJECT:
      return {
        ...state,
        currentProject: action.payload.project,
        currentProjectId: action.payload.projectId,
        loading: false,
        error: null,
      };

    case ProjectActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ProjectActionTypes.CLEAR_PROJECT:
      return {
        ...state,
        currentProject: null,
        currentProjectId: null,
        loading: false,
        error: null,
      };

    case ProjectActionTypes.SET_INITIALIZED:
      return {
        ...state,
        initialized: action.payload,
      };

    default:
      return state;
  }
}

// Storage utilities
const storage = {
  save(projectId: string) {
    localStorage.setItem('currentProjectId', projectId);
  },

  load(): string | null {
    return localStorage.getItem('currentProjectId');
  },

  clear() {
    localStorage.removeItem('currentProjectId');
  },
};

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const { organisationId, user } = useAuth();
  const [prevOrganisationId, setPrevOrganisationId] = useState<string | null>(null);
  const [shouldInitialize, setShouldInitialize] = useState(true);

  const [state, dispatch] = useReducer(projectReducer, {
    currentProject: null,
    currentProjectId: null,
    loading: false,
    error: null,
    initialized: false,
  });

  // Fetch project by ID
  const fetchProject = async (projectId: string): Promise<Project | null> => {
    if (!organisationId) {
      throw new Error('No organisation selected');
    }

    try {
      const response = await apiClient.get(
        `/organisations/${organisationId}/projects/${projectId}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Project not found');
        }
        throw new Error('Failed to fetch project');
      }

      const data = await response.json();
      return data.project;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  };

  // Clear project when organisation changes
  useEffect(() => {
    if (prevOrganisationId && prevOrganisationId !== organisationId) {
      console.log('Organisation changed, clearing project');
      storage.clear();
      dispatch({ type: ProjectActionTypes.CLEAR_PROJECT });
      setShouldInitialize(false); // Prevent initialization from running
    }
    setPrevOrganisationId(organisationId);
  }, [organisationId]);

  // Load initial project state on mount
  useEffect(() => {
    const initializeProject = async () => {
      // Don't initialize until we have auth data
      if (!user || !organisationId) {
        dispatch({ type: ProjectActionTypes.SET_INITIALIZED, payload: true });
        return;
      }

      // Don't initialize if we just switched organizations
      if (!shouldInitialize) {
        dispatch({ type: ProjectActionTypes.SET_INITIALIZED, payload: true });
        setShouldInitialize(true); // Reset for next time
        return;
      }

      const storedProjectId = storage.load();

      if (!storedProjectId) {
        dispatch({ type: ProjectActionTypes.SET_INITIALIZED, payload: true });
        return;
      }

      dispatch({ type: ProjectActionTypes.SET_LOADING, payload: true });

      try {
        const project = await fetchProject(storedProjectId);
        if (project) {
          dispatch({
            type: ProjectActionTypes.SET_PROJECT,
            payload: { project, projectId: storedProjectId },
          });
        } else {
          // Project not found, clear stored ID
          storage.clear();
          dispatch({ type: ProjectActionTypes.CLEAR_PROJECT });
        }
      } catch (error) {
        console.error('Failed to load stored project:', error);
        // Clear invalid stored project
        storage.clear();
        dispatch({ type: ProjectActionTypes.SET_ERROR, payload: 'Failed to load project' });
      } finally {
        dispatch({ type: ProjectActionTypes.SET_INITIALIZED, payload: true });
      }
    };

    initializeProject();
  }, [user, organisationId, shouldInitialize]);

  const setCurrentProject = async (projectId: string): Promise<void> => {
    dispatch({ type: ProjectActionTypes.SET_LOADING, payload: true });

    try {
      const project = await fetchProject(projectId);
      if (project) {
        storage.save(projectId);
        dispatch({
          type: ProjectActionTypes.SET_PROJECT,
          payload: { project, projectId },
        });
      } else {
        throw new Error('Project not found');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set project';
      dispatch({ type: ProjectActionTypes.SET_ERROR, payload: errorMessage });
      throw error;
    }
  };

  const clearCurrentProject = () => {
    storage.clear();
    dispatch({ type: ProjectActionTypes.CLEAR_PROJECT });
  };

  const refreshCurrentProject = async (): Promise<void> => {
    if (!state.currentProjectId) {
      return;
    }

    dispatch({ type: ProjectActionTypes.SET_LOADING, payload: true });

    try {
      const project = await fetchProject(state.currentProjectId);
      if (project) {
        dispatch({
          type: ProjectActionTypes.SET_PROJECT,
          payload: { project, projectId: state.currentProjectId },
        });
      } else {
        // Project no longer exists
        clearCurrentProject();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh project';
      dispatch({ type: ProjectActionTypes.SET_ERROR, payload: errorMessage });
      throw error;
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        ...state,
        setCurrentProject,
        clearCurrentProject,
        refreshCurrentProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
